import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { forkJoin, map, Observable, tap, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Data } from '../../graphql/models/gqlModels';
import { AfrigisAddress } from '../../models/afrigis';
import { IEmailType, IRelationship } from '../../models/user';

export interface MedicalAidProvider {
  id: string;
  defaultItem: boolean;
  description: string;
  refDataType: string;
  shortCode: string;
}

interface CountryResponse {
  id: string;
  code: string;
  name: string;
}

interface DialCodeResponse {
  country_code: string;
  country_name: string;
  dialing_code: string;
}

export interface Country extends CountryResponse {
  flagurl: string;
  dialCode: string;
}

@Injectable({
  providedIn: 'root',
})
export class LookupService {
  medicalAidProviders = signal<MedicalAidProvider[]>([]);
  countries = signal<Country[]>([]);
  relationships = signal<IRelationship[]>([]);
  maritalStatusList = signal<string[]>([]);
  titleList = signal<IRelationship[]>([]);
  emailTypes = signal<IEmailType[]>([]);
  // phoneTypes = signal<IPhoneNumber[]>([]);
  parentalStatusList = signal<IRelationship[]>([]);
  relationshipToStudentList = signal<IRelationship[]>([]);
  phoneTypes = signal<IRelationship[]>([]);
  idTypes = signal<IRelationship[]>([]);

  constructor(
    private readonly http: HttpClient,
    private readonly apollo: Apollo,
  ) {}

  getMedicalAidProviders() {
    this.http
      .get<MedicalAidProvider[]>(`${environment.apiBaseUrl}/enrolment/ref/medicalaid`)
      .pipe(tap((providers) => this.medicalAidProviders.set(providers)))
      .subscribe({
        error: (err) => console.error('Error loading medical aid providers:', err),
      });
  }

  getSchoolCodeMedicalAidProviders(schoolCode: string) {
    this.http
      .get<MedicalAidProvider[]>(`${environment.apiBaseUrl}/enrolment/ref/medicalaid/${schoolCode}`)
      .pipe(tap((providers) => this.medicalAidProviders.set(providers)))
      .subscribe({
        error: (err) => console.error('Error loading medical aid providers for school code:', err),
      });
  }

  getCountriesWithDialCodes() {
    const countries$ = this.http.get<CountryResponse[]>(`${environment.apiBaseUrl}/enrolment/ref/countries`);
    const dialCodes$ = this.http.get<DialCodeResponse[]>(`${environment.apiBaseUrl}/composed`);

    forkJoin([countries$, dialCodes$])
      .pipe(
        map(([countries, dialCodes]) => {
          return countries.map((country) => {
            // Find matching dial code by country code (assuming codes match between APIs)
            const dialCodeInfo = dialCodes.find((dc) => dc.country_code.toLowerCase() === country.code.toLowerCase());

            return {
              id: country.id,
              code: country.code,
              name: country.name,
              dialCode: this.getDialCode(dialCodeInfo?.dialing_code),
              flagurl: `https://flagsapi.com/${country.code.toUpperCase()}/flat/64.png`,
            };
          });
        }),
        tap((countriesWithDialCodes: Country[]) => {
          this.countries.set(countriesWithDialCodes);
        }),
      )
      .subscribe({
        error: (err) => console.error('Error loading countries with dial codes:', err),
      });
  }

  getDialCode(dialCode: string | undefined): string {
    //if dial code contains a '+' sign, return it as is
    if (dialCode && dialCode.startsWith('+')) {
      return dialCode;
    }
    //if dial dialCode does not contain a '+' sign, add it
    return `+${dialCode}`;
  }

  getRelationships(): void {
    this.http
      .get<[]>(`${environment.apiBaseUrl}/enrolment/ref/guardian/relationship`)
      .pipe(tap((relations) => this.relationships.set(relations)))
      .subscribe({
        error: (err) => console.error('Error loading relationships:', err),
      });
  }

  getEmailTypes(): void {
    this.http
      .get<[]>(`${environment.apiBaseUrl}/enrolment/ref/email`)
      .pipe(tap((emailTypes) => this.emailTypes.set(emailTypes)))
      .subscribe({
        error: (err) => console.error('Error loading email types:', err),
      });
  }

  getPhoneNumberTypes(): void {
    this.http
      .get<[]>(`${environment.apiBaseUrl}/enrolment/ref/phone`)
      .pipe(tap((phoneTypes) => this.phoneTypes.set(phoneTypes)))
      .subscribe({
        error: (err) => console.error('Error loading phone types:', err),
      });
  }

  getIdTypes(): void {
    this.getGraphQLReferenceTypes('ID_TYPE', (value) => {
      this.idTypes.set(value);
    });
  }

  getMartialStatusList(): void {
    this.http
      .get<[]>(`${environment.apiBaseUrl}/enrolment/ref/maritalstatus`)
      .pipe(
        tap((martialStatus) => {
          this.maritalStatusList.set(martialStatus);
        }),
      )
      .subscribe({
        error: (err) => console.error('Error loading martial status:', err),
      });
  }

  getGraphQLReferenceTypes(refDataType: string, signalSetter: (value: IRelationship[]) => void): void {
    this.apollo
      .query<Data>({
        query: gql`
          query GetRefDataByType($type: String!) {
            refDataItemsByType(refDataType: $type) {
              id
              description
            }
          }
        `,
        variables: {
          type: refDataType,
        },
      })
      .pipe(
        map((response) => {
          // Get the array from the specific data structure
          const items = response.data.refDataItemsByType;
          // Map to extract both id and description as IRelationship objects
          return (
            items?.map((item) => ({
              id: item.id,
              description: item.description,
            })) ?? []
          );
        }),
      )
      .subscribe({
        next: (relationships) => {
          signalSetter(relationships);
        },
        error: (err) => console.error(`Error loading ${refDataType} list:`, err),
      });
  }

  getAddress(query: string): Observable<AfrigisAddress[]> {
    const url = '/afrigis/FindStreetsStartingWith/' + query;
    return this.http.get<AfrigisAddress[]>(url).pipe(
      timeout(10000), // Set timeout to 10 seconds
    );
  }

  getTitleList(): void {
    this.getGraphQLReferenceTypes('TITLE', this.titleList.set.bind(this.titleList));
  }

  getParentalStatusList(): void {
    this.getGraphQLReferenceTypes('PARENTAL_STATUS', this.parentalStatusList.set.bind(this.parentalStatusList));
  }

  getRelationshipList(): void {
    this.getGraphQLReferenceTypes(
      'STUDENT_RELATIONSHIP_TYPE',
      this.relationshipToStudentList.set.bind(this.relationshipToStudentList),
    );
  }

  getRelationshipToStudentById(id: string): IRelationship | null {
    return this.relationshipToStudentList().find((relationship) => relationship.id === id) ?? null;
  }
  getParentalStatusById(id: string): IRelationship | undefined {
    return this.parentalStatusList().find((status) => status.id === id);
  }
  getTitleById(id: string): IRelationship | undefined {
    return this.titleList().find((title) => title.id === id);
  }
}
